import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

interface MetadataCardProps {
  metadata: Record<string, any>;
  onUpdate: (key: string, value: string) => void;
  onDelete: (key: string) => void;
}

export default function MetadataCard({ metadata, onUpdate, onDelete }: MetadataCardProps) {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const entries = Object.entries(metadata || {});

  const handleAdd = () => {
    if (!newKey.trim() || !newValue.trim()) return;
    onUpdate(newKey.trim(), newValue.trim());
    setNewKey('');
    setNewValue('');
  };

  return (
    <Card title="Custom Fields" padding="md">
      <div className="space-y-3">
        {/* Existing entries */}
        {entries.length > 0 ? (
          <div className="space-y-2">
            {entries.map(([key, value]) => (
              <div key={key} className="flex items-center justify-between gap-2 text-sm">
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-stone-700">{key}:</span>{' '}
                  <span className="text-stone-600">{String(value)}</span>
                </div>
                <button
                  onClick={() => onDelete(key)}
                  className="flex-shrink-0 p-1 text-stone-400 hover:text-red-500 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-stone-400">No custom fields.</p>
        )}

        {/* Add form */}
        <div className="flex items-end gap-2 border-t border-stone-100 pt-3">
          <Input
            placeholder="Key"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            className="text-sm"
          />
          <Input
            placeholder="Value"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className="text-sm"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleAdd}
            disabled={!newKey.trim() || !newValue.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
